import { ref } from 'vue';
import { getKnowledgeBaseEnabled } from '@/api/ParleyApi';

// The global Knowledge Base kill-switch (super-admin, Configuration page →
// System parameters). Off hides the Knowledge Base tab on both account detail
// pages AND blocks the two /rag routes; per-account RagConfig enablement is a
// separate gate that still applies inside the tab.
//
// Unlike useStudentCostObjectBillingFlag, which fetches once per call site,
// this caches the in-flight promise at MODULE level. Three consumers need the
// same answer for a single page load — the router guard plus whichever tab bar
// the route lands on — and a per-call-site fetch would issue a request for
// each. Worse, the guard and the tab bar could disagree if the switch were
// flipped between their two reads, admitting a navigation whose tab is then
// hidden.
let cached: Promise<boolean> | null = null;

/**
 * Resolves the flag, sharing one request across every caller. Fails closed:
 * an unreachable or erroring endpoint reads as "off", matching the API's own
 * behaviour when its SSM parameter cannot be read.
 */
export function fetchKnowledgeBaseEnabled(): Promise<boolean> {
  if (!cached) {
    cached = getKnowledgeBaseEnabled().catch(() => {
      // Fail closed for THIS read, but drop the cache so the next navigation
      // retries. Caching the rejection would hide the tab for the rest of the
      // session over one transient 500.
      cached = null;
      return false;
    });
  }
  return cached;
}

/**
 * Drops the cached answer so the next read re-fetches. Called after a
 * super-admin flips the switch, so the tab appears or disappears without a
 * full page reload — and by tests, which must not inherit each other's cache.
 */
export function resetKnowledgeBaseFlagCache(): void {
  cached = null;
}

/**
 * Component-facing wrapper. `loading` starts true so a tab bar renders no tab
 * at all until the answer arrives, rather than flashing one it is about to
 * remove.
 */
export function useKnowledgeBaseFlag() {
  const knowledgeBaseEnabled = ref(false);
  const loading = ref(true);

  async function loadKnowledgeBaseFlag() {
    try {
      knowledgeBaseEnabled.value = await fetchKnowledgeBaseEnabled();
    } finally {
      loading.value = false;
    }
  }

  return { knowledgeBaseEnabled, loading, loadKnowledgeBaseFlag };
}
